import type { BarcodeScanner, FoodSource } from "../ports/food-source";
import type { FoodRepository } from "../ports/repositories";
import {
  createImportedFoodDraft,
  type ExternalFoodCandidate,
  type ImportedFoodDraft,
} from "../../domain/foods/import-draft";
import type { Food } from "../../domain/foods/food";

export type BarcodeLookupResult =
  | { readonly kind: "local"; readonly barcode: string; readonly food: Food }
  | { readonly kind: "online"; readonly barcode: string; readonly draft: ImportedFoodDraft; readonly attribution?: string }
  | { readonly kind: "not_found"; readonly barcode: string };

export async function lookUpBarcode(
  dependencies: { readonly foods: FoodRepository; readonly sources: readonly FoodSource[] },
  barcodeValue: string,
): Promise<BarcodeLookupResult> {
  const barcode = barcodeValue.trim();
  if (!/^\d{6,14}$/.test(barcode)) throw new Error("Barcode must contain 6 to 14 digits");
  const local = await dependencies.foods.findByBarcode(barcode);
  if (local && !needsProviderMeasureRefresh(local)) return { kind: "local", barcode, food: local };
  for (const source of dependencies.sources) {
    try {
      const result = await source.findByBarcode(barcode);
      if (result && (!local || isProviderMeasureRefresh(local, result.candidate))) {
        return {
          kind: "online",
          barcode,
          draft: createImportedFoodDraft(result.candidate),
          attribution: result.attribution,
        };
      }
    } catch (error) {
      if (!local) throw error;
    }
  }
  if (local) return { kind: "local", barcode, food: local };
  return { kind: "not_found", barcode };
}

function needsProviderMeasureRefresh(
  food: Food,
): food is Food & { readonly provenance: Extract<Food["provenance"], { readonly kind: "imported" }> } {
  return food.provenance.kind === "imported" && (food.measures?.length ?? 0) === 0;
}

function isProviderMeasureRefresh(food: Food, candidate: ExternalFoodCandidate): boolean {
  return needsProviderMeasureRefresh(food)
    && (candidate.measures?.length ?? 0) > 0
    && food.provenance.provider === candidate.provider
    && food.provenance.providerFoodId === candidate.providerFoodId;
}

export async function scanAndLookUpBarcode(
  dependencies: {
    readonly scanner: BarcodeScanner;
    readonly foods: FoodRepository;
    readonly sources: readonly FoodSource[];
  },
  signal?: AbortSignal,
): Promise<BarcodeLookupResult> {
  const code = await dependencies.scanner.scan(signal);
  return lookUpBarcode(dependencies, code);
}
